/**
 * Global event-selector context for the admin SPA.
 *
 * Today's Event / Stages / Roster tabs each render their own
 * `<Select>` event picker. Confusing UX when the operator switches
 * tabs and finds a different event selected. Per wireframe
 * (`design-system/ui_kits/mvp/admin.html:204`) the brand-strip
 * carries an event pill between "TuviON · Admin" and the user pill;
 * this context is the data layer behind that pill.
 *
 * Persistence: `tuvion.admin.current_event_id` localStorage so the
 * selection survives reloads. Multi-tab admin sessions on the same
 * laptop will diverge until either operator picks a different event;
 * acceptable for MVP.
 *
 * Provider scope: `RequireSession`'s children. Public driver / viewer
 * paths (which `RequireSession` doesn't wrap) never load this context.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { EventRow, listEvents } from '@/services/fleet';

const STORAGE_KEY = 'tuvion.admin.current_event_id';
const POLL_INTERVAL_MS = 15_000;

interface CurrentEventCtx {
  /** All events the admin can pick from, newest-first. */
  events: EventRow[];
  /** Currently selected event id (read from localStorage on mount). */
  currentEventId: number | null;
  /** Currently selected event row, or null if events aren't loaded yet. */
  currentEvent: EventRow | null;
  /** Setter; persists to localStorage. */
  setCurrentEventId: (id: number | null) => void;
  /** Force a refresh of the events list (e.g. after EventTab create). */
  refresh: () => Promise<void>;
  /** True while the initial fetch is in flight. */
  loading: boolean;
}

const Ctx = createContext<CurrentEventCtx | null>(null);

function readStoredId(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeStoredId(id: number | null): void {
  try {
    if (id == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    // ignore quota
  }
}

export const CurrentEventProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [currentEventId, setCurrentEventIdState] = useState<number | null>(() =>
    readStoredId(),
  );
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    try {
      const data = await listEvents();
      setEvents(data);
      // If the stored selection no longer exists (deleted, etc),
      // fall back to the newest. Otherwise leave selection alone.
      setCurrentEventIdState((curr) => {
        if (curr != null && data.some((e) => e.id === curr)) return curr;
        const fallback = data[0]?.id ?? null;
        writeStoredId(fallback);
        return fallback;
      });
    } catch {
      // Leave events as-is on failure; consumers see stale data
      // rather than empty selector flashing.
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + slow background refresh so the navbar selector +
  // pending-count badge see new events that other operators create.
  useEffect(() => {
    void refresh();
    const id = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const setCurrentEventId = useCallback((id: number | null) => {
    setCurrentEventIdState(id);
    writeStoredId(id);
  }, []);

  const currentEvent = useMemo(
    () => events.find((e) => e.id === currentEventId) ?? null,
    [events, currentEventId],
  );

  const value = useMemo<CurrentEventCtx>(
    () => ({
      events,
      currentEventId,
      currentEvent,
      setCurrentEventId,
      refresh,
      loading,
    }),
    [events, currentEventId, currentEvent, setCurrentEventId, refresh, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useCurrentEvent(): CurrentEventCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      'useCurrentEvent() must be called inside <CurrentEventProvider>',
    );
  }
  return ctx;
}
